import React, { useMemo } from 'react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { useTheme } from '../../../stores/ThemeContext';

const ScoreBalance = ({ data }) => {
  const { isDark } = useTheme();

  const gridColor   = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const axisColor   = isDark ? '#a1a1aa' : '#52525b';
  const dimColor    = isDark ? '#71717a' : '#71717a';
  const legendColor = isDark ? '#a1a1aa' : '#52525b';
  const tooltipBg   = isDark ? '#1c1c1e' : '#ffffff';
  const tooltipBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.12)';
  const tooltipText = isDark ? '#fafafa' : '#111111';

  const chartData = useMemo(() => {
    if (!data?.score_balance) return null;

    const { last_30, all_time } = data.score_balance;
    if (!last_30.emotion && !last_30.risk && !last_30.strategy &&
        !all_time.emotion && !all_time.risk && !all_time.strategy) {
      return null;
    }

    return [
      { subject: 'Emotion', last30: last_30.emotion, allTime: all_time.emotion },
      { subject: 'Risk', last30: last_30.risk, allTime: all_time.risk },
      { subject: 'Strategy', last30: last_30.strategy, allTime: all_time.strategy },
    ];
  }, [data]);

  if (!chartData) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-sm font-semibold text-foreground mb-1">Score Balance</h3>
        <p className="text-xs text-muted-foreground mb-4">Emotion, Risk & Strategy averages</p>
        <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
          No scored journals yet. Complete journals with scores to see your balance.
        </div>
      </div>
    );
  }

  const CustomTick = ({ payload, x, y, textAnchor }) => {
    const entry = chartData.find(d => d.subject === payload.value);
    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill={axisColor}
        fontSize={12}
        fontWeight={600}
      >
        <tspan>{payload.value}</tspan>
        {entry && (
          <tspan x={x} dy={14} fontSize={10} fill={dimColor}>
            {entry.last30.toFixed(1)}
          </tspan>
        )}
      </text>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <h3 className="text-sm font-semibold text-foreground mb-1">Score Balance</h3>
      <p className="text-xs text-muted-foreground mb-4">Emotion, Risk & Strategy averages</p>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
          <PolarGrid stroke={gridColor} />
          <PolarAngleAxis
            dataKey="subject"
            tick={<CustomTick />}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={{ fill: dimColor, fontSize: 9 }}
            axisLine={false}
          />
          <Radar
            name="All Time"
            dataKey="allTime"
            stroke={dimColor}
            fill={dimColor}
            fillOpacity={0.15}
            strokeWidth={1}
            strokeDasharray="4 3"
          />
          <Radar
            name="Last 30 Sessions"
            dataKey="last30"
            stroke="#6366f1"
            fill="#6366f1"
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, color: legendColor }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: tooltipBg,
              border: tooltipBorder,
              borderRadius: 8,
              fontSize: 12,
              color: tooltipText,
            }}
            formatter={(value, name) => [`${value.toFixed(1)}`, name]}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScoreBalance;
